import MugImage from '@assets/images/custom/mug.svg';
import {
  AccountStates,
  EntityTypes,
  SubAccountStates,
  UserState,
} from '@generated/graphql';
import { getAccountName } from '@interfaces/account';
import type { FinancialProduct, Theme } from '@interfaces/financialProduct';
import { getGoalName } from '@interfaces/goal';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { User } from '@interfaces/user';
import { CheckCircle, Warning } from '@mui/icons-material';
import type { SxProps } from '@mui/material';
import { AccountTypes } from '@onevesthq/ov-enums';
import { Dialog } from '@ovComponents/2-component/dialog/dialog';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  ConfirmationModal,
  type ConfirmationModalProps,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { DeprecatedBadge } from '@ovComponents/3-pattern/deprecatedBadge/deprecatedBadge';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { legacyColors } from '@ovComponents/legacyColor';
import { Box, Typography } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { FinancialProductSelectionForm } from './financialProductSelection.visual';
import type { DraftFinancialProduct } from './interfaces';

const tableHeadSx = {
  textTransform: 'uppercase' as const,
  color: legacyColors.neutral600,
};
const tableRowSx: SxProps = {
  border: 'none',
  py: 2,
};

export const SubAccountsVisual = ({
  options,
  loading,
  subAccounts,
  activeSubAccountId,
  draftFinancialProducts,
  portfolioThemes,
  modelPortfolios,
  updateDraft,
  onSubAccountSelection,
  onPortfolioThemeChange,
  onDraftSubmission,
  onContinue,
}: {
  options: any;
  loading: boolean;
  subAccounts: SubAccount[];
  activeSubAccountId: string;
  draftFinancialProducts: { [subAccountId: string]: DraftFinancialProduct };
  portfolioThemes: Theme[];
  modelPortfolios: FinancialProduct[];
  updateDraft: (subAccountId: string, draft: DraftFinancialProduct) => void;
  onSubAccountSelection: (subAccount: SubAccount) => void;
  onPortfolioThemeChange: (theme: Theme) => void;
  onDraftSubmission: () => void;
  onContinue: () => void;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('client, workflowCompletions');
  const [open, setOpen] = useState(false);
  const [draftFinancialProduct, setDraftFinancialProduct] =
    useState<DraftFinancialProduct>();
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [warningModalProps, setWarningModalProps] =
    useState<ConfirmationModalProps>({
      open: false,
      onConfirm: () => {},
      onCancel: () => {},
      title: '',
      bodyText: '',
      confirmButtonLabel: '',
      cancelButtonLabel: '',
      confirmButtonColor: 'primary',
      showCancelButton: true,
    });

  useEffect(() => {
    setDraftFinancialProduct(draftFinancialProducts[activeSubAccountId]);
  }, [draftFinancialProducts, activeSubAccountId]);

  const handleModalClose = () => {
    setOpen(false);
  };

  const handleContinue = async () => {
    const numSubAcountsWithoutFinancialProducts = subAccounts.filter(
      (subAccount) => !subAccount.financialProduct,
    ).length;
    const requireAllProducts = options.requireAllSubAccountProducts;
    if (numSubAcountsWithoutFinancialProducts > 0) {
      const pluralSuffix = numSubAcountsWithoutFinancialProducts > 1 ? 's' : '';
      setWarningModalProps({
        open: true,
        onConfirm: async () => {
          setShowWarningModal(false);
          if (!requireAllProducts) {
            await onContinue();
          }
        },
        onCancel: () => setShowWarningModal(false),
        title: t('workflowCompletions:subAccounts.warningModal.title', {
          numSubAccounts: numSubAcountsWithoutFinancialProducts,
          pluralSuffix,
        }),
        bodyText: t('workflowCompletions:subAccounts.warningModal.body', {
          numSubAccounts: numSubAcountsWithoutFinancialProducts,
          pluralSuffix,
        }),
        confirmButtonLabel: t(
          `workflowCompletions:subAccounts.warningModal.${requireAllProducts ? 'returnButtonLabel' : 'confirmButtonLabel'}`,
        ),
        cancelButtonLabel: t(
          'workflowCompletions:subAccounts.warningModal.cancelButtonLabel',
        ),
        confirmButtonColor: 'primary',
        showCancelButton: !requireAllProducts,
      });
      setShowWarningModal(true);
    } else {
      await onContinue();
    }
  };

  return (
    <WorkflowCompletionForm
      onSubmit={handleContinue}
      submitButtonProps={{
        label: t('client:continue'),
        disabled: loading,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackendString(options?.title)}
        </Typography>
        <DeprecatedBadge />
      </Box>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        {!loading && subAccounts?.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <img src={MugImage} alt='mug-icon' />
            <Typography
              variant='bodyLarge'
              sx={{
                mt: 1,
                maxWidth: '500px',
                textAlign: 'center',
                mb: 3,
              }}
            >
              {t('workflowCompletions:subAccounts.summaryTable.noSubAccounts')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant='labelSmall' sx={tableHeadSx}>
                    {t('workflowCompletions:subAccounts.summaryTable.goal')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='labelSmall' sx={tableHeadSx}>
                    {t('workflowCompletions:subAccounts.summaryTable.account')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='labelSmall' sx={tableHeadSx}>
                    {t('workflowCompletions:subAccounts.summaryTable.product')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subAccounts?.map((subAccount: SubAccount) => (
                <TableRow
                  key={subAccount.id}
                  hover
                  sx={{
                    cursor: subAccount.financialProduct
                      ? 'not-allowed'
                      : 'pointer',
                  }}
                  onClick={() => {
                    if (subAccount.financialProduct) return;
                    onSubAccountSelection(subAccount);
                    setOpen(true);
                  }}
                >
                  <TableCell sx={tableRowSx}>
                    <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                      {getGoalName(subAccount.goal) ||
                        t('workflowCompletions:goals.goal')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableRowSx}>
                    <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                      {getAccountName(subAccount.account, false)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tableRowSx}>
                    <StatusTag
                      color={
                        subAccount.financialProduct ? 'positive' : 'warning'
                      }
                      icon={subAccount.financialProduct ? CheckCircle : Warning}
                      label={
                        subAccount.financialProduct
                          ? translateBackendString(
                              subAccount?.financialProduct?.translatedName,
                            )
                          : t(
                              'workflowCompletions:subAccounts.summaryTable.productNotSet',
                            )
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog
          sx={{
            '.MuiDialog-container': {
              '.MuiPaper-root': {
                padding: '20px',
              },
            },
          }}
          open={open}
          onClose={handleModalClose}
          fullWidth
        >
          <FinancialProductSelectionForm
            draftFinancialProduct={draftFinancialProduct}
            portfolioThemes={portfolioThemes}
            modelPortfolios={modelPortfolios}
            updateDraft={updateDraft}
            onPortfolioThemeChange={onPortfolioThemeChange}
            onClose={handleModalClose}
            onSubmit={onDraftSubmission}
          />
        </Dialog>
        {showWarningModal && <ConfirmationModal {...warningModalProps} />}
      </Box>
    </WorkflowCompletionForm>
  );
};

const exampleUser: User = {
  id: 'user',
  subAccounts: [],
  goals: [],
  accounts: [],
  type: EntityTypes.Individual,
  organization: { id: 'org' },
  state: UserState.Active,
  physicalAddress: undefined,
};

export const subAccountsVisualPropsExample = {
  options: {},
  loading: false,
  subAccounts: [
    {
      id: '1',
      state: SubAccountStates.Active,
      account: {
        id: 'account1',
        type: AccountTypes.TFSA,
        applyForGovFunds: [],
        state: AccountStates.Active,
        user: exampleUser,
      },
      goal: {
        id: 'goal1',
        name: 'Buy a car',
        user: exampleUser,
      },
      financialProduct: {
        translatedName: { en: 'Very Low Risk - Core' },
      },
    },
    {
      id: '2',
      state: SubAccountStates.Active,
      account: {
        id: 'account2',
        type: AccountTypes.RRSP,
        applyForGovFunds: [],
        state: AccountStates.Active,
        user: exampleUser,
      },
      goal: {
        id: 'goal2',
        name: 'Buy a house',
        user: exampleUser,
      },
    },
  ],
  activeSubAccountId: '',
  draftFinancialProducts: {},
  portfolioThemes: [],
  modelPortfolios: [],
  updateDraft: () => {},
  onDraftSubmission: () => {},
  onSubAccountSelection: () => {},
  onPortfolioThemeChange: () => {},
  onContinue: () => {},
};

export default SubAccountsVisual;
